import React, { useState } from "react"
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from "@mui/material"
import Tooltip from "@mui/material/Tooltip"
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps"

const FIPS_TO_ABBR = {
  1: "AL",
  2: "AK",
  4: "AZ",
  5: "AR",
  6: "CA",
  8: "CO",
  9: "CT",
  10: "DE",
  11: "DC",
  12: "FL",
  13: "GA",
  15: "HI",
  16: "ID",
  17: "IL",
  18: "IN",
  19: "IA",
  20: "KS",
  21: "KY",
  22: "LA",
  23: "ME",
  24: "MD",
  25: "MA",
  26: "MI",
  27: "MN",
  28: "MS",
  29: "MO",
  30: "MT",
  31: "NE",
  32: "NV",
  33: "NH",
  34: "NJ",
  35: "NM",
  36: "NY",
  37: "NC",
  38: "ND",
  39: "OH",
  40: "OK",
  41: "OR",
  42: "PA",
  44: "RI",
  45: "SC",
  46: "SD",
  47: "TN",
  48: "TX",
  49: "UT",
  50: "VT",
  51: "VA",
  53: "WA",
  54: "WV",
  55: "WI",
  56: "WY",
}

const getUSAbbr = (geo) => {
  if (geo.properties?.postal) return geo.properties.postal.toUpperCase()
  if (geo.properties?.abbr) return geo.properties.abbr.toUpperCase()
  if (geo.properties?.STUSPS) return geo.properties.STUSPS.toUpperCase()
  return FIPS_TO_ABBR[parseInt(geo.id, 10)] || null
}

const TravelMapView = ({
  name,
  visitedUsa = [],
  visitedWorld = [],
  onToggle,
  readOnly = false,
  getTooltipExtra,
  loading = false,
  fillColor = "#2196f3",
}) => {
  const [scope, setScope] = useState("usa")

  const visited = scope === "usa" ? visitedUsa : visitedWorld

  const handleClick = (geo) => {
    if (readOnly || !onToggle) return
    const abbr = getUSAbbr(geo)
    const id = scope === "usa" ? abbr : geo.properties?.ISO_A3 || geo.id
    if (!id) return
    onToggle(scope, id)
  }

  const geoUrl =
    scope === "usa"
      ? "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json"
      : "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json"
  const projection = scope === "usa" ? "geoAlbersUsa" : "geoEqualEarth"

  const handleScopeChange = (event, newScope) => {
    if (newScope) setScope(newScope)
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
        <Typography variant="h5">Travel Map for {name}</Typography>
        <ToggleButtonGroup
          value={scope}
          exclusive
          onChange={handleScopeChange}
          sx={{ ml: 2 }}
        >
          <ToggleButton value="usa">USA</ToggleButton>
          <ToggleButton value="world">World</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.100",
        }}
      >
        <ComposableMap
          projection={projection}
          style={{ width: "100%", height: "100%" }}
        >
          <ZoomableGroup key={scope}>
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const abbr = getUSAbbr(geo)
                  const geoId =
                    scope === "usa" ? abbr : geo.properties.ISO_A3 || geo.id
                  const isVisited = visited.includes(geoId)
                  const geoName =
                    scope === "usa"
                      ? geo.properties.name
                      : geo.properties.name ||
                        geo.properties.NAME ||
                        geo.properties.ADMIN ||
                        geo.id

                  let tooltipText = geoName
                  if (getTooltipExtra && isVisited) {
                    const visitors = getTooltipExtra(scope, geoId)
                    if (visitors && visitors.length > 0) {
                      tooltipText = `${geoName} - Visited by: ${visitors.join(", ")}`
                    }
                  }

                  return (
                    <Tooltip
                      key={geo.rsmKey}
                      title={tooltipText}
                      placement="top"
                      arrow
                    >
                      <g onClick={() => handleClick(geo)}>
                        <Geography
                          geography={geo}
                          style={{
                            default: {
                              fill: isVisited ? fillColor : "#ECEFF1",
                              stroke: "#607D8B",
                              strokeWidth: 0.5,
                              outline: "none",
                            },
                            hover: {
                              fill: isVisited ? fillColor : "#CFD8DC",
                              cursor: readOnly ? "default" : "pointer",
                              outline: "none",
                            },
                            pressed: {
                              fill: isVisited ? fillColor : "#CFD8DC",
                              outline: "none",
                            },
                          }}
                        />
                      </g>
                    </Tooltip>
                  )
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </Box>
    </Box>
  )
}

export default TravelMapView
